import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import '../style.css';

/* ── Live concurrent counter (polls blog_api every 30s) ── */
function useConcurrent() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    const fetch_ = () =>
      fetch('https://culturlingua.com/blog_api.php?action=concurrent')
        .then(r => r.json()).then(d => setCount(d.concurrent ?? null)).catch(() => {});
    fetch_();
    const iv = setInterval(fetch_, 30000);
    return () => clearInterval(iv);
  }, []);
  return count;
}

/* ── Animated count-up ── */
function CountUp({ target, suffix = '', duration = 1200 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const pct = Math.min((now - start) / duration, 1);
        setVal(Math.round(pct * target));
        if (pct < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

const LEVELS = [
  { code: 'A1', name: 'Principiante', color: '#6b95f5', desc: 'Puedes presentarte, hacer preguntas simples y entender frases cotidianas.' },
  { code: 'A2', name: 'Básico', color: '#3b6fe0', desc: 'Te comunicas en situaciones rutinarias y describes tu entorno inmediato.' },
  { code: 'B1', name: 'Intermedio', color: '#f5c842', desc: 'Entiendes textos estándar y puedes desenvolverte en la mayoría de situaciones de viaje.' },
  { code: 'B2', name: 'Intermedio Alto', color: '#f5a623', desc: 'Te expresas con fluidez y puedes interactuar con hablantes nativos sin esfuerzo.' },
  { code: 'C1', name: 'Avanzado', color: '#e05c3b', desc: 'Usas el idioma con flexibilidad para fines académicos, profesionales y sociales.' },
  { code: 'C2', name: 'Maestría', color: '#1a1744', desc: 'Dominas el inglés con la misma precisión que un hablante nativo culto.' },
];

const FAQS = [
  {
    q: '¿Cuánto tiempo tarda el examen de ubicación?',
    a: 'Depende de tu nivel. La evaluación tiene 60 preguntas que aumentan en dificultad, pero no necesitas responderlas todas — cuando llegues a preguntas que no sabes, simplemente detienes el examen. La mayoría de personas termina en 10 a 20 minutos.',
  },
  {
    q: '¿Necesito crear una cuenta?',
    a: 'No necesitas crear una cuenta. Solo ingresa tu nombre, correo electrónico, teléfono y edad antes de comenzar. Esos datos nos permiten enviarte tu resultado y asignarte al grupo correcto.',
  },
  {
    q: '¿El examen incluye speaking?',
    a: 'Sí, dependiendo de tu puntaje en la parte escrita, el sistema puede solicitarte una breve prueba de speaking evaluada por inteligencia artificial. Te recomendamos hacer el examen en un lugar tranquilo por si aparece esta sección.',
  },
  {
    q: '¿Tengo que responder todas las preguntas?',
    a: 'No. El objetivo es conocer tu nivel real, no adivinar. Cuando llegues a preguntas que no puedes responder con seguridad, es mejor detener la evaluación en ese punto — así el resultado es más preciso.',
  },
  {
    q: '¿Qué tan confiable es el resultado?',
    a: 'El examen está alineado con el Marco Común Europeo de Referencia (MCER). Las preguntas aumentan progresivamente en dificultad y, en algunos casos, incluyen una prueba de speaking con IA para afinar el diagnóstico. El resultado refleja con precisión tus habilidades actuales.',
  },
  {
    q: '¿Qué hago después de conocer mi nivel?',
    a: 'Con tu nivel asignado, puedes explorar los cursos de Culturlingua diseñados para cada etapa o agendar una asesoría gratuita con nuestro equipo para encontrar el curso que mejor se adapte a tus objetivos y horarios.',
  },
  {
    q: '¿Puedo hacer el examen desde el celular?',
    a: 'Sí, el examen está optimizado para móviles, tabletas y computadoras. Si la prueba de speaking aparece, asegúrate de tener micrófono disponible y conexión estable.',
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{ borderBottom: '1px solid #e5e7eb', padding: '20px 0', cursor: 'pointer' }}
      onClick={() => setOpen(!open)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--navy)', margin: 0, fontFamily: 'DM Sans, sans-serif' }}>{q}</h3>
        <span style={{ fontSize: '1.25rem', color: 'var(--blue)', flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(45deg)' : 'rotate(0)' }}>+</span>
      </div>
      {open && <p style={{ marginTop: '12px', color: 'var(--muted)', lineHeight: '1.7', fontSize: '0.95rem' }}>{a}</p>}
    </div>
  );
}

function ExamenUbicacion() {
  const location   = useLocation();
  const params     = new URLSearchParams(location.search);
  const fromBlog   = params.get('from') === 'blog';
  const blogSlug   = params.get('slug') || '';
  const sessionId  = params.get('sid')  || '';
  const concurrent = useConcurrent();

  // Build exam URL — carry session_id and slug so linguahub can POST back to funnel_complete
  const examHref = (() => {
    const p = new URLSearchParams({
      utm_source:   'sitio',
      utm_medium:   fromBlog ? 'cta' : 'pagina',
      utm_campaign: fromBlog ? 'blog_post' : 'examen_ubicacion',
    });
    if (sessionId) p.set('cl_sid',  sessionId);
    if (blogSlug)  p.set('cl_slug', blogSlug);
    return `https://linguahub.culturlingua.com/gral/placement_test/examen.php?${p.toString()}`;
  })();

  // Urgency: spots counter (static but credible)
  // Urgency: spots counter removed per user preference

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': 'https://culturlingua.com/Examen-de-ubicacion/',
        url: 'https://culturlingua.com/Examen-de-ubicacion/',
        name: 'Examen de Ubicación de Inglés Gratis | Culturlingua Tlaquepaque',
        description: 'Descubre tu nivel de inglés A1-C2 gratis. Evaluación con gramática, vocabulario y speaking con IA. Alineada al MCER. Para adultos, jóvenes y profesionales en Guadalajara y México.',
        inLanguage: 'es-MX',
        isPartOf: { '@id': 'https://culturlingua.com/' },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://culturlingua.com/' },
            { '@type': 'ListItem', position: 2, name: 'Inglés', item: 'https://culturlingua.com/Cursos' },
            { '@type': 'ListItem', position: 3, name: 'Examen de Ubicación', item: 'https://culturlingua.com/Examen-de-ubicacion/' },
          ],
        },
      },
      {
        '@type': 'EducationalOrganization',
        '@id': 'https://culturlingua.com/#org',
        name: 'Culturlingua',
        url: 'https://culturlingua.com/',
        logo: 'https://culturlingua.com/logo.webp',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Reforma 31',
          addressLocality: 'Tlaquepaque',
          addressRegion: 'Jalisco',
          postalCode: '45500',
          addressCountry: 'MX',
        },
        telephone: '+52-33-3344-9139',
        sameAs: [
          'https://www.facebook.com/Culturlinguagdl',
          'https://www.instagram.com/culturlingua_gdl/',
        ],
      },
      {
        '@type': 'Quiz',
        name: 'Examen de Ubicación de Inglés — Culturlingua',
        description: 'Evaluación gratuita de nivel de inglés con 60 preguntas progresivas de gramática, vocabulario y comprensión, más una prueba de speaking con IA. Alineada al MCER (A1–C2).',
        url: examHref,
        educationalLevel: 'A1, A2, B1, B2, C1, C2',
        provider: { '@id': 'https://culturlingua.com/#org' },
        timeRequired: 'PT15M',
        isAccessibleForFree: true,
        inLanguage: 'es-MX',
        about: { '@type': 'Language', name: 'English', alternateName: 'en' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQS.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      },
    ],
  };

  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Examen de Ubicación de Inglés Gratis | Culturlingua Tlaquepaque</title>
        <meta name="description" content="Descubre tu nivel de inglés A1-C2 gratis. Evaluación con gramática, vocabulario y speaking con IA, alineada al MCER. Sin costo. Para adultos y jóvenes en Guadalajara, Jalisco y todo México." />
        <meta name="keywords" content="examen de ubicación de inglés, test de nivel inglés gratis, evaluar nivel de inglés, prueba de inglés online, nivel MCER inglés, examen inglés Guadalajara, test inglés Tlaquepaque, diagnóstico inglés México, speaking inglés IA" />
        <link rel="canonical" href="https://culturlingua.com/Examen-de-ubicacion/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Examen de Ubicación de Inglés Gratis | Culturlingua" />
        <meta property="og:description" content="Descubre tu nivel de inglés A1-C2 gratis. Incluye gramática, vocabulario y speaking con IA. Sin costo. Resultado inmediato." />
        <meta property="og:url" content="https://culturlingua.com/Examen-de-ubicacion/" />
        <meta property="og:image" content="https://culturlingua.com/logo.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="es_MX" />
        <meta property="og:site_name" content="Culturlingua" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Examen de Ubicación de Inglés Gratis | Culturlingua" />
        <meta name="twitter:description" content="Descubre tu nivel de inglés A1-C2 gratis. Incluye speaking con IA. Sin costo. Resultado inmediato." />
        <meta name="twitter:image" content="https://culturlingua.com/logo.webp" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ── HERO ── */}
      <div className="page-hero" style={{ paddingBottom: '80px', position: 'relative', overflow: 'hidden' }}>
        <style>{`
          @keyframes heroFloat{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-18px) scale(1.04)}}
          @keyframes heroPulse{0%,100%{opacity:.07}50%{opacity:.14}}
          @keyframes fadeSlideUp{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:none}}
          .hero-orb-1{position:absolute;top:-80px;right:-60px;width:400px;height:400px;background:radial-gradient(circle,rgba(245,200,66,.12),transparent 70%);border-radius:50%;animation:heroPulse 4s ease-in-out infinite;pointer-events:none;}
          .hero-orb-2{position:absolute;bottom:-100px;left:-80px;width:320px;height:320px;background:radial-gradient(circle,rgba(59,111,224,.1),transparent 70%);border-radius:50%;animation:heroPulse 5s ease-in-out infinite 1s;pointer-events:none;}
          .hero-anim-1{animation:fadeSlideUp .5s ease both;}
          .hero-anim-2{animation:fadeSlideUp .55s ease .1s both;}
          .hero-anim-3{animation:fadeSlideUp .55s ease .2s both;}
          .hero-anim-4{animation:fadeSlideUp .55s ease .3s both;}
          .hero-anim-5{animation:fadeSlideUp .55s ease .4s both;}
        `}</style>
        <div className="hero-orb-1"/><div className="hero-orb-2"/>

        {fromBlog && (
          <div className="hero-anim-1" style={{ background:'rgba(245,200,66,.15)',border:'1px solid rgba(245,200,66,.3)',borderRadius:'100px',padding:'6px 18px',display:'inline-flex',alignItems:'center',gap:'8px',marginBottom:'16px',fontSize:'.82rem',color:'var(--gold)',fontWeight:700 }}>
            <span>📖</span> Llegaste desde el blog — tu resultado te espera
          </div>
        )}

        <div className="hero-anim-2">
          <div className="section-tag" style={{ marginBottom: '16px' }}>Gratis · Alineado al MCER · Speaking con IA</div>
        </div>
        <h1 className="hero-anim-3">Examen de Ubicación de Inglés</h1>
        <p className="hero-anim-4" style={{ maxWidth: '640px', margin: '16px auto 0' }}>
          Descubre tu nivel real de inglés — de A1 a C2 — con nuestra evaluación gratuita.
          Incluye gramática, vocabulario y, según tu puntaje, una prueba de speaking evaluada por inteligencia artificial.
        </p>
        <div className="hero-anim-4" style={{ display:'flex',gap:'12px',flexWrap:'wrap',justifyContent:'center',marginTop:'28px' }}>
          {['🆓 Completamente gratis','📝 60 preguntas','🎤 Speaking con IA','🏆 Resultado inmediato','🎓 MCER A1–C2'].map(p => (
            <span key={p} style={{ background:'rgba(255,255,255,.1)',border:'1px solid rgba(255,255,255,.18)',padding:'6px 16px',borderRadius:'100px',fontSize:'.85rem',color:'rgba(255,255,255,.9)',fontWeight:500 }}>{p}</span>
          ))}
        </div>
        <div className="hero-anim-5" style={{ display:'flex',gap:'32px',justifyContent:'center',flexWrap:'wrap',marginTop:'36px' }}>
          {[{value:500,suffix:'+',label:'alumnos colocados'},{value:98,suffix:'%',label:'precisión de nivel'},{value:10,suffix:' min',label:'promedio de duración'}].map(({value,suffix,label})=>(
            <div key={label} style={{ textAlign:'center' }}>
              <div style={{ fontFamily:"'Fraunces',serif",fontSize:'2rem',fontWeight:900,color:'var(--gold)',lineHeight:1 }}><CountUp target={value} suffix={suffix}/></div>
              <div style={{ fontSize:'.78rem',color:'rgba(255,255,255,.5)',marginTop:'4px' }}>{label}</div>
            </div>
          ))}
          {concurrent !== null && concurrent > 0 && (
            <div style={{ textAlign:'center' }}>
              <div style={{ fontFamily:"'Fraunces',serif",fontSize:'2rem',fontWeight:900,color:'#6ee7b7',lineHeight:1,display:'flex',alignItems:'center',justifyContent:'center',gap:'8px' }}>
                <span style={{ width:'10px',height:'10px',borderRadius:'50%',background:'#6ee7b7',display:'inline-block',animation:'heroPulse 1.5s ease-in-out infinite' }}/>
                {concurrent}
              </div>
              <div style={{ fontSize:'.78rem',color:'rgba(255,255,255,.5)',marginTop:'4px' }}>en el sitio ahora</div>
            </div>
          )}
        </div>
      </div>

      {/* ── CTA PRINCIPAL ── */}
      <section style={{ background:'var(--cream)',padding:'60px 24px' }}>
        <div style={{ maxWidth:'820px',margin:'0 auto' }}>
          <div style={{ background:'white',border:'1px solid #e5e7eb',borderRadius:'16px',padding:'32px',marginBottom:'12px' }}>
            <p style={{ fontSize:'12px',color:'var(--muted)',marginBottom:'6px',textTransform:'uppercase',letterSpacing:'.08em' }}>Evaluación gratuita</p>
            <h2 style={{ fontSize:'1.35rem',fontWeight:700,color:'var(--navy)',marginBottom:'10px',lineHeight:1.3,fontFamily:'Fraunces, serif' }}>¿En qué nivel de inglés estás?</h2>
            <p style={{ fontSize:'.9rem',color:'var(--muted)',lineHeight:1.7,marginBottom:'20px' }}>Responde hasta donde puedas — el examen para automáticamente cuando llegas a tu límite. No adivines: el resultado es más preciso si te detienes cuando ya no sabes.</p>
            <div style={{ display:'flex',flexWrap:'wrap',gap:'8px',marginBottom:'24px' }}>
              {[{label:'Gramática y vocabulario',bg:'#e8f0ff'},{label:'Comprensión lectora',bg:'#e8f5ee'},{label:'Speaking con IA',bg:'#fef3e2',sub:'según tu puntaje'}].map(({label,bg,sub})=>(
                <div key={label} style={{ display:'flex',alignItems:'center',gap:'8px',background:'var(--cream)',border:'1px solid #e5e7eb',borderRadius:'8px',padding:'8px 14px' }}>
                  <div style={{ width:'28px',height:'28px',borderRadius:'6px',background:bg,flexShrink:0 }}/>
                  <div><div style={{ fontSize:'13px',color:'var(--text)' }}>{label}</div>{sub&&<div style={{ fontSize:'11px',color:'var(--muted)' }}>{sub}</div>}</div>
                </div>
              ))}
            </div>
            <a href={examHref} target="_blank" rel="noopener noreferrer" className="btn-glow"
              style={{ display:'block',textAlign:'center',fontSize:'1rem',padding:'15px 24px',borderRadius:'10px' }}
              aria-label="Hacer el examen de ubicación de inglés gratis en Culturlingua">
              Comenzar examen →
            </a>
            <div style={{ display:'flex',gap:'16px',justifyContent:'center',flexWrap:'wrap',marginTop:'12px' }}>
              {['10–20 min','Sin registro','Gratis'].map((label,i,arr)=>(
                <React.Fragment key={label}>
                  <span style={{ fontSize:'12px',color:'var(--muted)' }}>{label}</span>
                  {i<arr.length-1&&<span style={{ width:'4px',height:'4px',borderRadius:'50%',background:'#d1d5db',alignSelf:'center',display:'inline-block' }}/>}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div style={{ background:'white',border:'1px solid #e5e7eb',borderRadius:'12px',padding:'14px 20px',marginBottom:'12px',display:'flex',alignItems:'center',gap:'14px',flexWrap:'wrap' }}>
            <div style={{ display:'flex' }}>
              {['#1a1744','#3b6fe0','#f5c842','#22c55e','#e05c3b'].map((c,i)=>(
                <div key={i} style={{ width:'28px',height:'28px',borderRadius:'50%',background:c,border:'2px solid white',marginLeft:i===0?0:'-8px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.6rem',fontWeight:800,color:i===2?'#1a1744':'white',zIndex:5-i }}>
                  {['MR','LG','AP','KF','SC'][i]}
                </div>
              ))}
            </div>
            <span style={{ fontSize:'.83rem',color:'#555' }}><strong style={{ color:'var(--navy)' }}>+500 estudiantes</strong> han descubierto su nivel con este examen</span>
            <div style={{ marginLeft:'auto',display:'flex',gap:'4px' }}>
              {[1,2,3,4,5].map(s=><span key={s} style={{ color:'#f5c842',fontSize:'14px' }}>★</span>)}
              <span style={{ fontSize:'.78rem',color:'#888',marginLeft:'4px' }}>4.9</span>
            </div>
          </div>
          <div style={{ background:'rgba(59,111,224,.04)',borderLeft:'2px solid var(--blue)',borderRadius:'0 8px 8px 0',padding:'12px 16px',display:'flex',gap:'10px',alignItems:'flex-start' }}>
            <div style={{ width:'18px',height:'18px',borderRadius:'50%',background:'var(--blue)',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',marginTop:'1px' }}>
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 2v4M6 8.5v1" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </div>
            <p style={{ margin:0,fontSize:'.85rem',color:'var(--text)',lineHeight:1.6 }}><strong>No adivines.</strong> El examen está diseñado para parar donde termina tu nivel real — así te asignamos al grupo correcto desde el primer día.</p>
          </div>
        </div>
      </section>

      {/* ── POR QUÉ HACER EL EXAMEN ── */}
      <section style={{ padding: '72px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag" style={{ marginBottom: '12px' }}>¿Para qué sirve?</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'var(--navy)', fontWeight: '900' }}>
              Por qué hacer el examen de ubicación
            </h2>
            <p style={{ color: 'var(--muted)', marginTop: '12px', maxWidth: '560px', margin: '12px auto 0', lineHeight: '1.7' }}>
              Conocer tu nivel real es el primer paso para aprender inglés de forma eficiente.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
            {[
              { icon: '🎯', title: 'Entra al grupo correcto', desc: 'Evita aburrirte en clases demasiado fáciles o frustrarte en niveles que aún no son para ti. El grupo correcto acelera tu aprendizaje.' },
              { icon: '💼', title: 'Certifica tu nivel para trabajo o estudio', desc: 'Muchas empresas y universidades piden evidencia de nivel de inglés. El examen te da un punto de referencia oficial alineado al MCER.' },
              { icon: '📈', title: 'Mide tu progreso', desc: 'Si ya estudiaste inglés antes, el examen te muestra exactamente dónde quedaste y cuánto te falta para alcanzar tu meta.' },
              { icon: '🌎', title: 'Prepárate para certificaciones', desc: 'Identifica tu nivel actual antes de inscribirte a TOEFL, IELTS o Cambridge. Sabrás cuánto tiempo de preparación necesitas.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} style={{ background: 'var(--cream)', borderRadius: '16px', padding: '28px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{icon}</div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: '700', color: 'var(--navy)', marginBottom: '8px' }}>{title}</h3>
                <p style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: '1.6' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NIVELES MCER ── */}
      <section style={{ padding: '72px 24px', background: 'var(--navy)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag" style={{ background: 'rgba(245,200,66,0.15)', color: 'var(--gold)', borderColor: 'rgba(245,200,66,0.3)', marginBottom: '12px' }}>
              Marco Común Europeo de Referencia
            </div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'white', fontWeight: '900' }}>
              ¿Qué nivel de inglés tienes?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '12px', maxWidth: '500px', margin: '12px auto 0', lineHeight: '1.7' }}>
              El MCER define 6 niveles internacionales de dominio del idioma. El examen identifica en cuál estás.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {LEVELS.map(({ code, name, color, desc }) => (
              <div key={code} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <span style={{ background: color, color: 'white', fontWeight: '800', fontSize: '0.85rem', padding: '4px 10px', borderRadius: '8px', flexShrink: 0, marginTop: '2px', fontFamily: 'DM Sans, sans-serif' }}>
                  {code}
                </span>
                <div>
                  <h3 style={{ color: 'white', fontWeight: '700', fontSize: '1rem', marginBottom: '6px' }}>{name}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', lineHeight: '1.6' }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CÓMO FUNCIONA ── */}
      <section style={{ padding: '72px 24px', background: 'white' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag" style={{ marginBottom: '12px' }}>Proceso</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'var(--navy)', fontWeight: '900' }}>
              ¿Cómo funciona el examen?
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {[
              { step: '1', title: 'Ingresa tus datos', desc: 'Proporciona tu nombre, correo, teléfono y edad. Eso nos permite enviarte tu resultado y asignarte al grupo correcto.' },
              { step: '2', title: 'Responde las preguntas', desc: '60 preguntas de gramática, vocabulario y comprensión lectora que aumentan progresivamente en dificultad. Para cuando ya no sepas una respuesta — no adivines.' },
              { step: '3', title: 'Prueba de speaking (si aplica)', desc: 'Dependiendo de tu puntaje escrito, el sistema puede pedirte una breve prueba de speaking evaluada por inteligencia artificial. Por eso recomendamos un lugar tranquilo.' },
              { step: '4', title: 'Recibe tu nivel y recomendación', desc: 'Obtienes tu nivel A1-C2 con una descripción de tus habilidades y el curso de Culturlingua recomendado para ti.' },
            ].map(({ step, title, desc }, i, arr) => (
              <div key={step} style={{ display: 'flex', gap: '24px', paddingBottom: i < arr.length - 1 ? '32px' : '0', position: 'relative' }}>
                {i < arr.length - 1 && (
                  <div style={{ position: 'absolute', left: '19px', top: '44px', bottom: 0, width: '2px', background: '#e5e7eb' }} />
                )}
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--blue)', color: 'white', fontWeight: '800', fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1, fontFamily: 'DM Sans, sans-serif' }}>
                  {step}
                </div>
                <div style={{ paddingTop: '8px' }}>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: '700', color: 'var(--navy)', marginBottom: '6px' }}>{title}</h3>
                  <p style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: '1.6' }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: '72px 24px', background: 'var(--cream)' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag" style={{ marginBottom: '12px' }}>Preguntas frecuentes</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'var(--navy)', fontWeight: '900' }}>
              Todo lo que necesitas saber
            </h2>
          </div>
          <div>
            {FAQS.map(({ q, a }) => (
              <FaqItem key={q} q={q} a={a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section style={{ padding: '72px 24px', background: 'var(--navy)', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'white', fontWeight: '900', marginBottom: '16px' }}>
            ¿Listo para descubrir tu nivel?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.65)', marginBottom: '32px', lineHeight: '1.7' }}>
            La evaluación es gratuita, tarda entre 10 y 20 minutos, y te da un diagnóstico completo: escritura y speaking. Busca un lugar tranquilo y comienza cuando quieras.
          </p>
          <a
            href={examHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-glow"
            style={{ display: 'inline-block', fontSize: '1.1rem', padding: '18px 40px', borderRadius: '12px' }}
            aria-label="Empezar el examen de ubicación de inglés gratis"
          >
            Empezar el Examen Ahora →
          </a>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '32px' }}>
            <Link to="/Cursos" className="btn-outline" style={{ color: 'rgba(255,255,255,0.8)', border: '1.5px solid rgba(255,255,255,0.25)' }}>
              Ver cursos de inglés
            </Link>
            <Link to="/contacto" className="btn-outline" style={{ color: 'rgba(255,255,255,0.8)', border: '1.5px solid rgba(255,255,255,0.25)' }}>
              Hablar con un asesor
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ExamenUbicacion;